import { cn } from '@cloud/shared/ui'
import appCss from '@cloud/theme/themes/globals.css?url'
import { TanStackDevtools } from '@tanstack/react-devtools'
import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, HeadContent, Scripts } from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { Providers } from '@/providers'
import { QueryProvider } from '@/providers/query-provider'
import { UserProvider } from '@/providers/user-provider'
import TanStackQueryDevtools from '../integrations/tanstack-query/devtools'
// @ts-expect-error — CSS-only side-effect import has no TS declaration
import '@fontsource/geist' // Sans
// @ts-expect-error — CSS-only side-effect import has no TS declaration
import '@fontsource/geist-mono' // Mono
import { GTProvider } from 'gt-react'
import { Component, type ReactNode, StrictMode } from 'react'
import { Toaster } from 'sonner'
import enDictionary from '../../../../packages/shared/src/i18n/translations/en.json'
import gtConfig from '../../gt.config.json'
import loadDictionary from '../lib/loadDictionary'

/**
 * Real React error boundary. Catches errors in ANY descendant component
 * — providers, route outlets, everything. The route-level `errorComponent`
 * only catches errors inside the route; this is the last line of defense.
 *
 * Logs the error with full detail to console so devtools has a real
 * stack, even if Vite's [Client] forwarding strips the substitutions.
 */
class ShellErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Use multiple console.error calls so at least one survives Vite's
    // format-string forwarding quirks.
    console.error('[ShellErrorBoundary] Caught error:', error)
    console.error('[ShellErrorBoundary] Error message:', error.message)
    console.error('[ShellErrorBoundary] Error stack:', error.stack)
    if (info.componentStack) {
      console.error('[ShellErrorBoundary] Component stack:', info.componentStack)
    }
  }

  render() {
    if (this.state.error) {
      const err = this.state.error
      return (
        <div
          style={{
            fontFamily: 'system-ui, sans-serif',
            padding: '2rem',
            maxWidth: '800px',
            margin: '2rem auto',
            color: '#1f2937',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
            An error occurred while rendering. Details below and in the browser console.
          </p>
          <pre
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              padding: '1rem',
              borderRadius: '6px',
              overflow: 'auto',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              color: '#991b1b',
            }}
          >
            {err.message || String(err)}
            {err.stack ? `\n\n${err.stack}` : ''}
          </pre>
          <div style={{ marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => {
                this.setState({ error: null })
              }}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                background: 'white',
                cursor: 'pointer',
                marginRight: '0.5rem',
              }}
            >
              Try again
            </button>
            <a
              href="/login"
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                background: '#2563eb',
                color: 'white',
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              Back to sign in
            </a>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export interface MyRouterContext {
  queryClient: QueryClient
}

/**
 * Root error boundary. TanStack Router calls this whenever any descendant
 * route or component throws an uncaught error. Without this, errors bubble
 * to the <AwaitInner> internal component with no useful stack.
 *
 * Keep this component deliberately minimal and free of providers/context
 * — if the app's provider tree is what crashed, a fancy error UI would
 * crash again. Just show the message and a reset link.
 */
function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  // Log to console so we always get a full stack in devtools
  console.error('[RootErrorBoundary]', error)
  return (
    <html lang="en">
      <head>
        <title>Something went wrong</title>
      </head>
      <body
        style={{
          fontFamily: 'system-ui, sans-serif',
          padding: '2rem',
          maxWidth: '640px',
          margin: '0 auto',
          color: '#1f2937',
        }}
      >
        <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
          The page hit an error while rendering. The console has the full stack trace.
        </p>
        <pre
          style={{
            background: '#f3f4f6',
            padding: '1rem',
            borderRadius: '6px',
            overflow: 'auto',
            fontSize: '0.8rem',
            whiteSpace: 'pre-wrap',
          }}
        >
          {error?.message || String(error)}
          {error?.stack ? `\n\n${error.stack}` : ''}
        </pre>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={reset}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <a
            href="/login"
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              background: '#2563eb',
              color: 'white',
              textDecoration: 'none',
            }}
          >
            Back to sign in
          </a>
        </div>
      </body>
    </html>
  )
}

/**
 * Default 404 page. Shown when a URL doesn't match any route. TanStack
 * Router also uses this when a loader throws `notFound()`. Keeping it
 * minimal so the root route stays deps-free — any richer 404 UI can be
 * a dedicated /404 route, which still exists.
 */
function RootNotFoundComponent() {
  return (
    <div
      style={{
        fontFamily: 'system-ui, sans-serif',
        padding: '2rem',
        maxWidth: '640px',
        margin: '4rem auto',
        textAlign: 'center',
        color: '#1f2937',
      }}
    >
      <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>404</h1>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
        The page you're looking for doesn't exist.
      </p>
      <a
        href="/dashboard"
        style={{
          padding: '0.5rem 1rem',
          borderRadius: '4px',
          background: '#2563eb',
          color: 'white',
          textDecoration: 'none',
        }}
      >
        Back to dashboard
      </a>
    </div>
  )
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Headpat Panel',
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
    ],
  }),
  errorComponent: RootErrorComponent,
  notFoundComponent: RootNotFoundComponent,
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body
        className={cn(
          'bg-background text-foreground antialiased wrap-anywhere font-sans', // Set background, text, , anti-aliasing styles, and overscroll behavior
        )}
      >
        <ShellErrorBoundary>
          <StrictMode>
            <QueryProvider>
              {/*
                UserProvider is hoisted to the root so it's available
                on every route — public ones (/login, /register,
                /forgot-password) included. The provider is smart
                about public paths: a 401 from whoami on /login just
                clears local state instead of triggering the
                full-logout recovery loop. Keeping it here is what
                lets the SignInForm (which calls `useUser()`) work
                from /login without duplicating logic.
              */}
              <UserProvider>
                <GTProvider
                  loadDictionary={loadDictionary}
                  config={gtConfig}
                  dictionary={enDictionary}
                  // We use local dictionaries, not GT cloud. Explicitly
                  // disable the cloud runtime + cache URLs so gt-react
                  // stops warning about a missing projectId.
                  runtimeUrl={null}
                  cacheUrl={null}
                >
                  <Providers>
                    {children}
                    <Toaster />
                  </Providers>
                  <TanStackDevtools
                    config={{
                      position: 'bottom-right',
                    }}
                    plugins={[
                      {
                        name: 'Tanstack Router',
                        render: <TanStackRouterDevtoolsPanel />,
                      },
                      TanStackQueryDevtools,
                    ]}
                  />
                </GTProvider>
              </UserProvider>
            </QueryProvider>
          </StrictMode>
        </ShellErrorBoundary>
        <Scripts />
      </body>
    </html>
  )
}
